import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Avatar,
  Typography,
  ListItemAvatar,
  useTheme,
  Chip,
  Collapse,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import "react-photo-view/dist/react-photo-view.css";
import { stringAvatar, stringToColor, nameToContactAvatarImage, NON_DRAGGABLE_AVATAR_IMG_PROPS } from "../../../config/AvatarColor";
import { formatChatTime } from "../../../utils/timestampUtils";
import { DARK_BUBBLE_TOOLTIP_SX } from "../../../utils/tooltipStyles";
import { renderWhatsAppFormattedText } from "../../../utils/whatsappTextFormatting";

import { ReactComponent as Sent } from "../../../Images/Inbox/ChatBox/Sent.svg";
import { ReactComponent as Read } from "../../../Images/Inbox/ChatBox/Read.svg";
import { ReactComponent as ErrorIcon } from "../../../Images/Inbox/ChatBox/Error.svg";
import VideoBox from "./ChatBoxFeatures/VideoBox";
import ImageViewer from "./ChatBoxFeatures/ImageViewer";
import MediaCollage from "./ChatBoxFeatures/ImageCollage";
import AudioBox from "./ChatBoxFeatures/AudioBox";
import DocumentBox from "./ChatBoxFeatures/DocumentBox";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
// Iconsax — ClusterDen's standard icon library (see [[icon-library-iconsax]]). Location/
// Personalcard/Call/PlayCircle/Speaker replace the MUI LocationOn/ContactPage/Phone/
// PlayCircleOutline/Campaign icons used across the location, contacts, and ad-referral cards;
// ShoppingCart/Gallery/ArrowDown2/ArrowUp2 replace ShoppingCartIcon/ImageIcon/ExpandMore/
// ExpandLessIcon in the redesigned catalog_purchase ("Product Order") card.
import {
  MessageProgramming,
  Clock,
  CloseCircle,
  Location,
  Personalcard,
  Call,
  PlayCircle,
  Speaker,
  Export,
  ShoppingCart,
  Gallery,
  ArrowDown2,
  ArrowUp2,
  Menu as MenuIcon,
} from "iconsax-react";
import PlatformIcon from "./PlatformIcon";
import TemplateMessageDisplay from "./TemplateMessageDisplay";
import CatalogMessageCard from "./ChatBoxFeatures/CatalogMessageCard";
import ListMessageCard from "./ChatBoxFeatures/ListMessageCard";
import { useContactSelection } from "../../../hooks/useContactSelection";
import { fetchInbox } from "../../../redux/Slice/Inbox/inboxRecentSlice";
import { getAuthToken } from "../../../utils/getAuthToken";
import { selectUserDetailsData } from "../../../redux/selectors";
import { isMetaMessengerTemplatePayload } from "./metaMessengerTemplateUtils";


const interactiveButtonRowSx = {
  border: "none",
  outline: "none",
  background: "none",
  margin: 0,
  font: "inherit",
  textAlign: "inherit",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 14px",
  color: "#1F7A5C",
  fontSize: "0.875rem",
  fontWeight: 500,
  cursor: "pointer",
};

const formatMessageTime = (timestamp) => {
  return formatChatTime(timestamp);
};

function locationPayloadFromParsed(p) {
  if (!p || typeof p !== "object" || Array.isArray(p)) return null;
  const lat = Number(p.latitude);
  const lng = Number(p.longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (
    p.productId ||
    p.catalogId ||
    p.items ||
    p.allProducts ||
    p.components ||
    p.catalogType ||
    p.productData ||
    Array.isArray(p.rows)
  ) {
    return null;
  }
  return {
    latitude: lat,
    longitude: lng,
    name: typeof p.name === "string" ? p.name : undefined,
    address: typeof p.address === "string" ? p.address : undefined,
  };
}

/** WhatsApp Cloud API stores location as JSON string or (sometimes) a plain object in Redux */
function parseWhatsAppLocationPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") {
    return locationPayloadFromParsed(raw);
  }
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  if (!t.startsWith("{")) return null;
  try {
    return locationPayloadFromParsed(JSON.parse(t));
  } catch {
    return null;
  }
}

function sharedContactsFromParsed(p) {
  if (!p || typeof p !== "object" || Array.isArray(p)) return null;
  if (!Array.isArray(p.contacts) || p.contacts.length === 0) return null;
  const first = p.contacts[0];
  if (!first || typeof first !== "object" || (!first.name && !first.phones)) return null;
  return { contacts: p.contacts };
}

/** WhatsApp shared contact message: { contacts: [ { name, phones, emails } ] } */
function parseWhatsAppSharedContactsPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === "object") {
    return sharedContactsFromParsed(raw);
  }
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  if (!t.startsWith("{")) return null;
  try {
    return sharedContactsFromParsed(JSON.parse(t));
  } catch {
    return null;
  }
}

function formatSharedContactDisplayName(c) {
  if (!c || !c.name || typeof c.name !== "object") {
    return "Contact";
  }
  const n = c.name;
  if (n.formatted_name && String(n.formatted_name).trim()) {
    return String(n.formatted_name).trim();
  }
  const parts = [n.prefix, n.first_name, n.middle_name, n.last_name, n.suffix].filter(
    (x) => x && String(x).trim()
  );
  return parts.length ? parts.map((x) => String(x).trim()).join(" ") : "Contact";
}

/** First phone with usable digits (wa_id preferred) for inbox open/create */
function primaryPhonePayloadForInbox(c) {
  const phones = Array.isArray(c?.phones) ? c.phones : [];
  const ph = phones.find((p) => p && (p.wa_id || p.phone)) || null;
  if (!ph) return null;
  const raw = ph.wa_id || ph.phone;
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits) return null;
  return { phoneNumberID: digits, displayLabel: ph.phone || ph.wa_id || digits };
}

// Lines shown before a plain text bubble offers "Read more" — same
// display:"-webkit-box"/WebkitLineClamp technique already used elsewhere in
// this file (reaction preview, catalog card), just applied to the main text
// bubble now that it actually needs it (long lorem-style messages otherwise
// render at unbounded height, see mockChatPreviewData.js m34).
const TEXT_MESSAGE_CLAMP_LINES = 8;

const ChatCard = ({
  sendername,
  clientname,
  time,
  message,
  replyBody,
  replyType,
  onReplyClick,
  replyId,
  status,
  messageType,
  replySenderName,
  storyUrl,
  storyId,
  storyReplyAvatarUrl,
  postMediaUrl,
  postPermalink,
  postMediaType,
  referralSourceType,
  referralHeadline,
  referralBody,
  referralMediaType,
  referralMediaUrl,
  reaction,
  reactionPreview,
  isInternalMention,
  senderAvatarColor,
  audioDurationSeconds,
  mediaThumbnailUrl,
  mediaFileSizeBytes,
  documentFileName,
  messagePlatform,
  contactId,
  messageId,
  metaMediaId,
  errorMessage,
  caption,
  // Consecutive-message grouping (pill-style bubbles): only text/chat call sites pass these.
  // Default (pillGrouping=false) preserves every other message type's existing layout untouched.
  pillGrouping = false,
  isGroupStart = true,
  isGroupEnd = true,
  // Whether this bubble belongs to a team-member chat, not a real contact
  // conversation (Rahim, 2026-08-27: "the chat panel uses the same pfp as a
  // contact would use but for a team member we never really changed it") —
  // gates the not-fromMe avatar below away from the contact mascot image,
  // same as everywhere else team members are shown (InboxAll.jsx's own list
  // row for them already uses stringAvatar, never the mascot).
  isTeamMemberChat = false,
}) => {
  const theme = useTheme();
  // CRITICAL: Check if this is an internal mention message FIRST
  // This must be checked before setting name and fromMe
  const isInternal = isInternalMention === true || status === "internal";
  
  // Use sendername or clientname - don't add fallback here
  // If both are missing, it means the message structure is incomplete
  // The Redux reducer should handle setting "Automated" for workflow messages
  // CRITICAL: For internal mentions, NEVER use "Automated" - always use sendername if available
  const name = isInternal && sendername 
    ? sendername  // For internal mentions, always use sendername if available
    : (sendername || clientname || "Automated");
  // Messages are "from me" only if they have sendername AND no clientname
  // If clientname exists, it's always from the client (left-aligned)
  // CRITICAL: Internal mentions with sendername should be "from me" (right-aligned)
  const fromMe = !!sendername && !clientname;

  /** WhatsApp-style emoji on the message bubble (not the separate Instagram/Facebook reaction row). */
  const showBubbleReaction =
    !!reaction?.emoji &&
    messageType !== "instagram_reaction" &&
    messageType !== "facebook_reaction";
  
  // CRITICAL: Removed infinite log - it was causing performance issues
  // The sendername issue will be fixed at the Redux level
  // Check if this is an automated message (workflow triggered) - robust detection for old and new messages
  // CRITICAL: Internal mentions should NEVER be considered automated messages
  const isAutomatedMessage = !isInternal && (
    (name && (name === "Automated" || name.toLowerCase() === "automated")) ||
                            (sendername && (sendername === "Automated" || sendername.toLowerCase() === "automated")) ||
    (clientname && clientname === "Automated" && !sendername)
  );
  
  // Get background color for internal mentions (use sender's avatar color with reduced intensity)
  const getMentionBackgroundColor = () => {
    if (isInternal && sendername && senderAvatarColor) {
      // Reduce intensity by lightening the color (blend with white)
      const hex = senderAvatarColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const lightR = Math.floor(r * 0.5 + 255 * 0.5);
      const lightG = Math.floor(g * 0.5 + 255 * 0.5);
      const lightB = Math.floor(b * 0.5 + 255 * 0.5);
      return `rgb(${lightR}, ${lightG}, ${lightB})`;
    } else if (isInternal && sendername) {
      // Fallback: generate color from sender name and lighten it
      const baseColor = stringToColor(sendername);
      const hex = baseColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const lightR = Math.floor(r * 0.5 + 255 * 0.5);
      const lightG = Math.floor(g * 0.5 + 255 * 0.5);
      const lightB = Math.floor(b * 0.5 + 255 * 0.5);
      return `rgb(${lightR}, ${lightG}, ${lightB})`;
    }
    // Regular WhatsApp green for user messages
    return fromMe ? "#D9FDD3" : theme.palette.background.paper;
  };
  
  // Render message with mention highlighting (works for all messages - user mentions and team member mentions)
  const renderMessageWithMentions = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Extract mentions by finding @ and extracting full mention names
    // This handles multi-word names like "Mohsin Ali"
    const parts = [];
    let lastIndex = 0;
    let mentionIndex = 0;
    
    while ((mentionIndex = text.indexOf('@', mentionIndex)) !== -1) {
      // Add text before the mention
      if (mentionIndex > lastIndex) {
        parts.push({ type: 'text', content: text.substring(lastIndex, mentionIndex) });
      }
      
      // Find the end of the mention
      // Strategy: Extract from @ until we find a space followed by a lowercase letter (next word)
      // This handles multi-word names like "Mohsin Ali" correctly
      // For "ok @Mohsin Ali done": extracts "@Mohsin Ali" (stops at space before "done")
      let endIndex = mentionIndex + 1;
      
      while (endIndex < text.length) {
        const char = text[endIndex];
        
        if (char === ' ') {
          // Found a space - check what comes after it
          let nextCharIndex = endIndex + 1;
          // Skip any additional spaces
          while (nextCharIndex < text.length && text[nextCharIndex] === ' ') {
            nextCharIndex++;
          }
          
          if (nextCharIndex >= text.length) {
            // End of string - include everything up to here (including the space)
            endIndex = text.length;
            break;
          }
          
          const nextChar = text[nextCharIndex];
          // If next char is a lowercase letter, it's likely the start of the next word
          // End the mention here (before this space, don't include the space)
          if (/[a-z]/.test(nextChar)) {
            // Space followed by lowercase - end of mention (don't include this space)
            break;
          }
          
          // Space followed by uppercase or other char - might be part of mention
          // Include this space and continue to the next word
          endIndex = endIndex + 1; // Include the space
          // Continue to extract the next word (like "Ali" in "Mohsin Ali")
        } else {
          endIndex++;
        }
      }
      
      // Extract the mention (including @)
      const mention = text.substring(mentionIndex, endIndex);
      parts.push({ type: 'mention', content: mention });
      
      lastIndex = endIndex;
      mentionIndex = endIndex + 1;
    }
    
    // Add remaining text after last mention
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.substring(lastIndex) });
    }
    
    // If no mentions found, still apply bold formatting to the plain text
    if (parts.length === 0) {
      return renderWhatsAppFormattedText(text);
    }
    
    // Determine blue color intensity based on theme mode
    const isDarkMode = theme.palette.mode === 'dark';
    // Light mode: darker blue for better contrast on light green (#0d47a1), Dark mode: much darker blue (#1565c0)
    const mentionColor = isDarkMode ? '#1565c0' : '#0d47a1';
    
    return parts.map((part, index) => {
      if (part.type === 'mention') {
        return (
          <span key={index} style={{ color: mentionColor, fontWeight: 500 }}>
            {part.content}
          </span>
        );
      }
      return <span key={index}>{renderWhatsAppFormattedText(part.content, `m${index}-`)}</span>;
    });
  };
  
  // State for multiple items dropdown
  const [showItems, setShowItems] = useState(false);
  const [buttonsDropdownOpen, setButtonsDropdownOpen] = useState(false);

  // "Read more" for long plain-text messages — clamped to TEXT_MESSAGE_CLAMP_LINES
  // by default, expandable in place. isTextClamped is measured (not assumed from
  // message.length) since the same character count wraps to very different line
  // counts depending on the bubble's current maxWidth (panel-resize dependent).
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isTextClamped, setIsTextClamped] = useState(false);
  const textMessageRef = useRef(null);
  // Mirrors isTextExpanded for the ResizeObserver callback below, which is set up
  // once per `message` (not re-created every expand/collapse toggle) — the callback
  // needs the CURRENT expanded state without the effect depending on it.
  const isTextExpandedRef = useRef(false);
  useEffect(() => {
    isTextExpandedRef.current = isTextExpanded;
  }, [isTextExpanded]);
  // Bug fix (2026-09-01, Rahim: "Show more/Show less do nothing" — a literal
  // 2-line message still showed "Read more"): reading scrollHeight WHILE
  // `-webkit-line-clamp` is active is unreliable once the clamped element's content
  // is split across multiple inline children instead of one plain text node — which
  // every message here always is (the emoji-splitting regex above wraps every
  // segment, emoji or not, in its own <span>). Chromium's legacy `-webkit-box` +
  // `-webkit-line-clamp` box model can then report scrollHeight taller than the
  // content actually renders, permanently flagging short messages as clamped even
  // though there's nothing real to expand. Fix: measure with the clamp MOMENTARILY
  // switched off — plain block-layout scrollHeight (no -webkit-box quirk) is
  // reliable regardless of child element count — then immediately restore it, all
  // synchronously before paint so nothing visibly flashes. A ResizeObserver re-runs
  // this whenever the element's box size actually changes (layout settling, panel
  // drag-resize, font load), instead of trusting one measurement taken at an
  // arbitrary moment.
  useLayoutEffect(() => {
    const el = textMessageRef.current;
    if (!el) {
      setIsTextClamped(false);
      return;
    }
    const measure = () => {
      // Deliberately skip while expanded — expanding removes the clamp, which
      // makes scrollHeight===clientHeight and would wrongly flip isTextClamped
      // back to false (hiding "Show less").
      if (isTextExpandedRef.current) return;
      const prevLineClamp = el.style.webkitLineClamp;
      const prevOverflow = el.style.overflow;
      el.style.webkitLineClamp = "unset";
      el.style.overflow = "visible";
      const naturalHeight = el.scrollHeight;
      el.style.webkitLineClamp = prevLineClamp;
      el.style.overflow = prevOverflow;
      // Live-measured (2026-09-01): a real single-line message read scrollHeight:24
      // vs clientHeight:22 with computed line-height:21.45px — a ~2px gap from
      // `-webkit-box`'s own sub-pixel rounding on a fractional line-height, not real
      // hidden content. A flat "+1" tolerance was too tight to absorb that, so short
      // messages were flagged clamped with nothing to actually expand. A genuine
      // hidden line adds close to a FULL line-height (~21px here), never just 2px —
      // so tolerate up to half a line-height as rounding noise; anything past that
      // is real overflow.
      const lineHeightPx = parseFloat(window.getComputedStyle(el).lineHeight);
      const roundingTolerancePx = Number.isFinite(lineHeightPx) ? lineHeightPx / 2 : 4;
      setIsTextClamped(naturalHeight > el.clientHeight + roundingTolerancePx);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [message]);

  /** Instagram / Facebook Messenger structured IMAGE attachments still get their own white
   * bubble. Templates share WhatsApp's green outer bubble instead — the platform is already
   * obvious from the conversation itself. The "black on green" clash Rahim flagged was the
   * template's own inner card going dark in dark mode, not the green wrapper — fixed at the
   * card level instead (MetaStructuredTemplateDisplay's CARD_BG now stays white always). */
  const metaStructuredBody =
    typeof message === "string" && message.trim().startsWith("{") && isMetaMessengerTemplatePayload(message);
  const useMetaAttachmentBubble =
    !isInternal &&
    metaStructuredBody &&
    messageType === "image";

  // Instagram story reply: image load error (ephemeral URL may expire)
  const [storyImageError, setStoryImageError] = useState(false);
  const [locationMapError, setLocationMapError] = useState(false);

  const whatsappLocation = parseWhatsAppLocationPayload(message);
  const showWhatsAppLocation =
    !!whatsappLocation &&
    (messageType === "location" ||
      messageType === "text" ||
      messageType === "chat" ||
      messageType === undefined ||
      messageType === null ||
      messageType === "");

  const whatsappSharedContacts = parseWhatsAppSharedContactsPayload(message);
  const showSharedContacts =
    !!whatsappSharedContacts &&
    (messageType === "contacts" ||
      messageType === "text" ||
      messageType === "chat" ||
      messageType === undefined ||
      messageType === null ||
      messageType === "");

  const dispatch = useDispatch();
  const { accountid, userid } = useParams();
  const userDetails = useSelector(selectUserDetailsData);
  // The chat this card is in: a contact opened from a shared-contact card is created on the SAME WhatsApp number as that
  // chat (a QR chat -> a QR contact on its number); a Meta chat stays exactly as before.
  const openChatContact = useSelector((state) => state.inboxInfo?.contactInfo);
  const { selectContact } = useContactSelection();
  const [openingSharedContactKey, setOpeningSharedContactKey] = useState(null);
  const sharedContactOpenLockRef = useRef(false);

  const openInboxChatForSharedContact = useCallback(
    async (c, rowKey) => {
      const primary = primaryPhonePayloadForInbox(c);
      if (!primary || !rowKey) return;
      if (sharedContactOpenLockRef.current) return;
      sharedContactOpenLockRef.current = true;
      setOpeningSharedContactKey(rowKey);
      try {
        const token = await getAuthToken();
        if (!token) {
          console.error("[ChatCard] open-by-phone: no auth token");
          return;
        }
        const serverDomain = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
        const res = await fetch(`${serverDomain}/api/contacts/open-by-phone`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            phoneNumberID: primary.phoneNumberID,
            name: formatSharedContactDisplayName(c),
            ...(openChatContact?.platform === "qr" && openChatContact?.connectionId
              ? { platform: "qr", connectionId: openChatContact.connectionId }
              : {}),
          }),
        });
        let data = {};
        try {
          data = await res.json();
        } catch {
          data = {};
        }
        if (!res.ok) {
          console.error("[ChatCard] open-by-phone failed:", data?.error || res.status);
          return;
        }
        const contactId = data.contact?._id || data.contact?.id;
        if (!contactId) return;
        const effectiveUserid = userid || userDetails?._id;
        await selectContact(String(contactId), {
          navigate: true,
          accountid,
          userid: effectiveUserid,
          clearPrevious: true,
        });
        if (data.created) {
          dispatch(fetchInbox());
        }
      } finally {
        sharedContactOpenLockRef.current = false;
        setOpeningSharedContactKey(null);
      }
    },
    [accountid, userid, userDetails?._id, selectContact, dispatch, openChatContact?.platform, openChatContact?.connectionId]
  );

  useEffect(() => {
    setLocationMapError(false);
  }, [message]);
  React.useEffect(() => {
    setStoryImageError(false);
  }, [storyUrl]);

  // Detect interactive reply buttons message stored as JSON string
  // WhatsApp: type === "button" with buttons. Instagram: type === "quick_reply" with body + buttons
  let interactiveButtonsPayload = null;
  if (typeof message === "string") {
    const trimmed = message.trim();
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && Array.isArray(parsed.buttons)) {
          if (parsed.type === "button") {
            interactiveButtonsPayload = parsed;
          } else if (parsed.type === "quick_reply") {
            // Instagram quick reply: body + buttons (same card layout, body optional)
            interactiveButtonsPayload = {
              body: parsed.body || "",
              buttons: parsed.buttons.map((b) => ({ id: b.payload || b.id, title: b.title || b.text, description: b.description })),
              footer: parsed.footer,
              header: parsed.header ? (typeof parsed.header === "object" ? parsed.header : { text: parsed.header }) : undefined,
            };
          }
        }
      } catch (_) {
        // Not JSON
      }
    }
  }

  // CRITICAL: If message is empty but we have messageType, log a warning
  if (messageType && !message && messageType !== "text") {
    console.warn("⚠️ ChatCard: messageType is", messageType, "but message is empty/falsy");
  }

  const renderStatusIcon = (status, useNewIcons = false) => {
    const iconColor =
      theme.palette.mode === "dark" ? theme.palette.text.secondary : "grey";

    // Normalize status for comparison
    const normalizedStatus = status ? String(status).toLowerCase().trim() : null;

    // DEBUG: Log all status values to find the issue
    // if (normalizedStatus === "read" || normalizedStatus === "seen") {
    //   console.log(`🔵 [CHATCARD STATUS] Rendering READ icon:`, {
    //     statusProp: status,
    //     normalizedStatus,
    //     time,
    //     messagePreview: typeof message === 'string' ? message.substring(0, 50) : 'not string'
    //   });
    // }

    switch (normalizedStatus) {
      case "pending":
        // Pill bubbles: Iconsax Clock instead of the MUI clock, sized up a little
        // (Rahim, 2026-08-25: "use new icon from our new library... increase the size a little").
        if (useNewIcons) {
          return <Clock size="16" variant="Linear" color={iconColor} />;
        }
        return (
          <AccessTimeIcon
            sx={{
              fontSize: { xs: "0.625rem", sm: "0.625rem", md: "0.75rem", lg: "0.75rem", xl: "0.75rem" },
              color: iconColor,
            }}
          />
        );
      case "sent":
        return <Sent />;
      case "delivered":
      case "received":
        // Reuse the exact same double-tick shape as "read" (Delivered.svg was a separate,
        // differently-proportioned asset — Rahim, 2026-08-25: "the delivered one is not the
        // same height as the read one... use a static one for both... only the colour changes").
        // Recoloring via CSS instead of the SVG's own baked-in stroke keeps this static + shared.
        return (
          <Box component="span" sx={{ display: "flex", height: "100%", "& path": { stroke: "#A9A9A9" } }}>
            <Read />
          </Box>
        );
      case "read":
      case "seen":
        return <Read />;
      case "error":
      case "failed":
        // Pill bubbles: Iconsax CloseCircle instead of the custom Error.svg, sized up a little.
        if (useNewIcons) {
          return (
            <Tooltip title={errorMessage || "Failed to send"} arrow placement="top" componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
              <CloseCircle size="16" variant="Bold" color="#C20000" style={{ cursor: "default" }} />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={errorMessage || "Failed to send"} arrow placement="top" componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
            <ErrorIcon style={{ cursor: "default" }} />
          </Tooltip>
        );
      default:
        return <Sent fill={iconColor} />;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        marginBottom: pillGrouping && !isGroupEnd
          ? { xs: 0.75, sm: 0.75, md: 1, lg: 1, xl: 1 }
          : showBubbleReaction
            ? { xs: 1.5, sm: 1.75, md: 2, lg: 1.75, xl: 1.75 }
            : { xs: 1, sm: 1.25, md: 1.5, lg: 1.25, xl: 1.25 },
        gap: pillGrouping ? 1.25 : { xs: 0.5, sm: 0.625, md: 0.75, lg: 0.75, xl: 0.75 },
        flexDirection: fromMe ? "row-reverse" : "row",
        alignSelf: fromMe ? "flex-end" : "flex-start",
      }}
    >
      {/* Side avatar, shown once per consecutive-sender group (on its first bubble, alongside
          the name — Rahim, 2026-08-25: "for two consecutive messages the name and pfp should
          show on to one time") rather than
          on every message — a plain spacer keeps the column width stable for the mid-group rows
          so bubbles stay aligned. Pill bubbles use the bigger sizing settled on this pass;
          non-pill types keep the original smaller size until they get the same treatment. */}
      <ListItemAvatar
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          minWidth: pillGrouping ? { xs: 36, sm: 36, md: 38, lg: 38, xl: 38 } : { xs: 44, sm: 48, md: 56, lg: 56, xl: 56 },
        }}
      >
        {pillGrouping && !isGroupStart ? (
          <Box sx={{ width: { xs: 27, sm: 27, md: 29, lg: 29, xl: 29 } }} />
        ) : (
        <Box sx={{ position: "relative", display: "flex", flexShrink: 0 }}>
        {isAutomatedMessage ? (
          <Box
            sx={{
              width: pillGrouping ? { xs: 27, sm: 27, md: 29, lg: 29, xl: 29 } : { xs: 32, sm: 28, md: 32, lg: 32, xl: 32 },
              height: pillGrouping ? { xs: 27, sm: 27, md: 29, lg: 29, xl: 29 } : { xs: 32, sm: 28, md: 32, lg: 32, xl: 32 },
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#EAF4FE",
              color: "#1E88E5",
            }}
          >
            {/* Bumped 16→19 (Rahim, 2026-08-27: "make the icon of automated a little
                big") — circle stays the same size, glyph alone grows; still clears
                the 27-29px pillGrouping circle with room to spare. */}
            <MessageProgramming size="19" variant="Linear" color="currentColor" />
          </Box>
        ) : /* Contact messages use the same mascot-image avatar as everywhere
              else contacts are shown (Inbox list row, Contact Details drawer)
              — this was the one real remaining spot still using stringAvatar
              (letter-initial) for the contact too, same as ChatBody.jsx's own
              special-case branches had (Rahim, 2026-08-22: "why is the chat
              not using the same emoji as the pfp"). fromMe (team member/
              agent/automated) keeps the initials avatar unchanged.
              isTeamMemberChat (2026-08-27, Rahim: "the chat panel uses the
              same pfp as a contact would use but for a team member we never
              really changed it") — the OTHER person in a team-member chat is
              also a team member, never a real contact, so the not-fromMe
              side needs the same letter-initial treatment as fromMe here,
              not the contact mascot fallback. Real contact conversations
              (isTeamMemberChat=false, the default) are completely untouched. */
        fromMe || isTeamMemberChat ? (
          (() => {
            const size = pillGrouping ? { xs: 27, sm: 27, md: 29, lg: 29, xl: 29 } : { xs: 32, sm: 28, md: 32, lg: 32, xl: 32 };
            const avatarProps = stringAvatar(name, size);
            return <Avatar {...avatarProps} sx={{ ...avatarProps.sx, flexShrink: 0 }} />;
          })()
        ) : (
          <Avatar
            src={nameToContactAvatarImage(name)}
            imgProps={NON_DRAGGABLE_AVATAR_IMG_PROPS}
            sx={{
              width: pillGrouping ? { xs: 27, sm: 27, md: 29, lg: 29, xl: 29 } : { xs: 32, sm: 28, md: 32, lg: 32, xl: 32 },
              height: pillGrouping ? { xs: 27, sm: 27, md: 29, lg: 29, xl: 29 } : { xs: 32, sm: 28, md: 32, lg: 32, xl: 32 },
              flexShrink: 0,
            }}
          />
        )}
        {/* Platform badge on the avatar corner: green WhatsApp icon for Cloud API messages,
            black for WhatsApp Webchat messages. Other platforms show no badge here. */}
        {(messagePlatform === "whatsapp" || messagePlatform === "whatsapp_web") && (
          <Box
            sx={{
              position: "absolute",
              bottom: -3,
              right: -3,
              display: "flex",
              borderRadius: "50%",
              border: (theme) => `1.5px solid ${theme.palette.background.default}`,
            }}
          >
            <PlatformIcon platform={messagePlatform} size={pillGrouping ? 14 : 15} sx={{ ml: 0 }} />
          </Box>
        )}
        </Box>
        )}
      </ListItemAvatar>

      <Box
        display={"flex"}
        flexDirection={"column"}
        flex={pillGrouping ? 1 : undefined}
        gap={{ xs: 0.25, sm: 0.375, md: 0.4375, lg: 0.375, xl: 0.375 }}
        alignItems={fromMe ? "flex-end" : "flex-start"}
        sx={showBubbleReaction ? { overflow: "visible", pb: 0.25 } : undefined}
      >
        {/* Name sits above the bubble, shown once on the first bubble of a consecutive-sender
            group (side avatar handles identity for the rest of the group). Pill bubbles use the
            bigger/regular-weight typography settled on this pass; non-pill types keep the
            original smaller bold subtitle2 until they get the same treatment. */}
        {isGroupStart && (
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={{ xs: 1, sm: 1.25, md: 1.5, lg: 1.25, xl: 1.25 }}
          >
            <Typography
              variant={pillGrouping ? "subtitle1" : "subtitle2"}
              sx={{
                fontWeight: pillGrouping ? 500 : "bold",
                marginBottom: pillGrouping ? 0 : { xs: 0.1, sm: 0.15, md: 0.175, lg: 0.15, xl: 0.15 },
                color: (theme) => theme.palette.text.primary,
                direction: fromMe ? "rtl" : "ltr",
                fontSize: pillGrouping
                  ? "0.8125rem"
                  : { xs: '0.6875rem', sm: '0.6875rem', md: '0.75rem', lg: '0.75rem', xl: '0.75rem' },
              }}
            >
              {name}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            // interactiveButtonsPayload used to force this outer pill transparent (Rahim,
            // 2026-08-27: "why is there no green background... its a send message" —
            // leftover from before the inner card was redesigned into a proper white
            // "document" card; back then the old card WAS the bubble itself, so a colored
            // pill around it would've double-nested. Now it follows the exact same
            // fromMe-colored-pill rule as catalog/list/template/everything else.
            backgroundColor: isInternal
              ? getMentionBackgroundColor()
              : useMetaAttachmentBubble
                ? (theme) => theme.palette.background.paper
                : fromMe
                  ? "#D9FDD3"
                  : (theme) => theme.palette.background.paper,
            // Bottom stayed 0 for the "generic"/"product" template sub-types, which render
            // transparent and rely on this outer box's own color/edge — leaving the date/tick
            // row directly underneath with no breathing room (Rahim, 2026-08-25: "add proper
            // spacing for the bottom so the date and tick icon has room to breath").
            // Audio used to carry its own smaller padding here, left over from before it was
            // wired into pillGrouping — now that it is (same flag drives every other media
            // type's spacing), it should just take the shared pillGrouping value like the
            // rest instead of a special case (Rahim, 2026-08-27: "spacing above the date is a
            // little too much keep as its for other cards").
            padding: useMetaAttachmentBubble
              ? "0 0 6px 0"
              : pillGrouping
                ? "10px 12px 5px 12px"
                : { xs: "5px 5px 2px 5px", sm: "6px 6px 3px 6px", md: "8px 8px 4px 8px", lg: "8px 8px 4px 8px", xl: "8px 8px 4px 8px" },
            borderRadius: useMetaAttachmentBubble
              ? "12px"
              : pillGrouping
                ? "10px"
                : "8px",
            position: "relative",
            gap: { xs: 0.375, sm: 0.5, md: 0.75, lg: 0.75, xl: 0.75 },
            overflow: useMetaAttachmentBubble || showBubbleReaction ? "visible" : "hidden",
            border: (theme) =>
              useMetaAttachmentBubble
                ? `1px solid ${theme.palette.mode === "dark" ? theme.palette.divider : "#e5e5e5"}`
                : !fromMe && theme.palette.mode === "dark"
                  ? `1px solid ${theme.palette.divider}`
                  : "none",
            maxWidth: useMetaAttachmentBubble
                ? "min(520px, calc(var(--chatbox-width, 100vw) - 96px))"
                : messageType === "audio"
                  ? { xs: "min(288px, calc(100vw - 88px))", sm: "auto", md: "auto", lg: "auto", xl: "auto" }
                  : replyBody
                    ? // 2026-09-12 — kept deliberately DIFFERENT from the long-text case
                      // below, confirmed by direct measurement this needs to be: a 50%
                      // cap (~601px on this pane) sits looser than the quote box's own
                      // ~400px natural wrapped width, so it never engages and the reply
                      // bubble reverts to the original bug (measured 403px, identical to
                      // pre-fix). The constraint here comes from the QUOTE's own width,
                      // not the pane's — needs to be tighter than that to matter at all.
                      // 280px (renders ~304px with padding) is the value already proven
                      // live to actually shrink it, forcing the quoted paragraph to wrap
                      // across more lines instead of dragging the bubble wide.
                      "min(280px, calc(var(--chatbox-width, 100vw) - 96px))"
                    : message && messageType === "text" && message.length > 100
                      ? // Same 50% cap, same reasoning as the replyBody branch above —
                        // was 42%, bumped to match for one consistent ratio across both
                        // "long content" cases instead of two different numbers.
                        "max(240px, min(50%, calc(var(--chatbox-width, 100vw) - 96px)))"
                      : "auto",
            minWidth: { xs: '60px', sm: '60px', md: '70px', lg: '70px', xl: '70px' },
            wordBreak: interactiveButtonsPayload ? "break-word" : undefined,
            overflowWrap: interactiveButtonsPayload ? "anywhere" : undefined,
           // Ensure short messages are not too narrow
          }}
        >
          {messageType === "story_reply" && (
            // Spacing/font-sizing pass only (Rahim, 2026-08-27: "just use better spacing
            // better font sizing as we have been doing... they only need that") — same
            // eyebrow-label size/weight (0.6875rem/600) as every card's small muted label,
            // a bit more breathing room between rows.
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: "0.6875rem",
                }}
              >
                Reply to story
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  maxWidth: 200,
                  minHeight: 140,
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                  bgcolor: (theme) => theme.palette.action.hover,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                {storyUrl && !storyImageError ? (
                  <Box
                    component="img"
                    src={storyUrl}
                    alt="Story"
                    onError={() => setStoryImageError(true)}
                    sx={{
                      width: "100%",
                      maxHeight: 280,
                      objectFit: "cover",
                      // Hides the broken/pending-image alt-text glitch — see the location
                      // card's fix for the full explanation.
                      color: "transparent",
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Story no longer available
                  </Typography>
                )}
                {/* Replier's avatar overlay (Instagram-style): profile pic from User Profile API, or initials from clientname (server sends contact.name for story_reply when clientname missing) */}
                {(storyReplyAvatarUrl || clientname) && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 6,
                      right: 6,
                      border: "2px solid",
                      borderColor: "background.paper",
                      borderRadius: "50%",
                      overflow: "hidden",
                      boxShadow: 1,
                    }}
                  >
                    {storyReplyAvatarUrl ? (
                      <Avatar
                        src={storyReplyAvatarUrl}
                        alt={clientname || "Replier"}
                        sx={{ width: 28, height: 28 }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      >
                        {(clientname || "?")[0]}
                      </Avatar>
                    ) : (
                      <Avatar
                        src={nameToContactAvatarImage(clientname || "?")}
                        imgProps={NON_DRAGGABLE_AVATAR_IMG_PROPS}
                        sx={{ width: 28, height: 28 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              {message && message !== "Reply to story" && (
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {message}
                </Typography>
              )}
            </Box>
          )}
          {messageType === "post_comment" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mb: 0.5,
              }}
            >
              {postMediaUrl && (
                <Box
                  component={postPermalink ? "a" : "div"}
                  href={postPermalink || undefined}
                  target={postPermalink ? "_blank" : undefined}
                  rel={postPermalink ? "noopener noreferrer" : undefined}
                  sx={{
                    width: "100%",
                    maxWidth: 200,
                    minHeight: 120,
                    borderRadius: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                    bgcolor: (theme) => theme.palette.action.hover,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                  }}
                >
                  <Box
                    component="img"
                    src={postMediaUrl}
                    alt="Post"
                    sx={{
                      width: "100%",
                      maxHeight: 280,
                      objectFit: "cover",
                      color: "transparent",
                    }}
                  />
                </Box>
              )}
              <Typography
                sx={{
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: "0.6875rem",
                }}
              >
                Comment on post
              </Typography>
              {message && (
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {message}
                </Typography>
              )}
            </Box>
          )}
          {messageType === "ctwa_referral" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mb: 0.5,
              }}
            >
              {referralMediaUrl && (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 200,
                    minHeight: 120,
                    borderRadius: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                    bgcolor: (theme) => theme.palette.action.hover,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <Box
                    component="img"
                    src={referralMediaUrl}
                    alt="Ad"
                    sx={{
                      width: "100%",
                      maxHeight: 280,
                      objectFit: "cover",
                      color: "transparent",
                    }}
                  />
                  {referralMediaType === "video" && (
                    <Box
                      sx={{
                        position: "absolute",
                        filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
                        display: "flex",
                      }}
                    >
                      <PlayCircle size={40} color="rgba(255,255,255,0.9)" variant="Bold" />
                    </Box>
                  )}
                </Box>
              )}
              <Typography
                sx={{
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: "0.6875rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Speaker size={14} color="currentColor" variant="Linear" />
                Message from WhatsApp Ad
              </Typography>
              {(referralHeadline || referralBody) && (
                <Box
                  sx={{
                    maxWidth: 200,
                    borderLeft: (theme) => `2px solid ${theme.palette.divider}`,
                    pl: 1.25,
                    py: 0.25,
                  }}
                >
                  {referralHeadline && (
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {referralHeadline}
                    </Typography>
                  )}
                  {referralBody && (
                    <Typography
                      color="text.secondary"
                      sx={{
                        display: "block",
                        mt: 0.25,
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {referralBody}
                    </Typography>
                  )}
                </Box>
              )}
              {message && (
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {message}
                </Typography>
              )}
            </Box>
          )}
          {messageType === "live_comment" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: "0.6875rem",
                }}
              >
                Live comment
              </Typography>
              {message && (
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.875rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {message}
                </Typography>
              )}
            </Box>
          )}
          {(messageType === "instagram_reaction" || messageType === "facebook_reaction") && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: (theme) => theme.palette.text.secondary,
                  fontSize: "0.6875rem",
                }}
              >
                Reacted to your message
              </Typography>
              {reactionPreview && (
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 200,
                    minHeight: 80,
                    borderRadius: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                    bgcolor: (theme) => theme.palette.action.hover,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {reactionPreview.messageType === "image" && reactionPreview.body && (
                    <Box
                      component="img"
                      src={reactionPreview.body}
                      alt="Preview"
                      sx={{ width: "100%", maxHeight: 200, objectFit: "cover", color: "transparent" }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  )}
                  {reactionPreview.messageType === "video" && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, p: 1 }}>
                      <VideoBox videoUrl={reactionPreview.body} />
                      {reactionPreview.caption && (
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", textAlign: "center" }} noWrap>
                          {reactionPreview.caption}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {reactionPreview.messageType === "audio" && (
                    <Box sx={{ p: 1, width: "100%" }}>
                      <AudioBox
                        audioUrl={reactionPreview.body}
                        initialDurationSeconds={reactionPreview.audioDurationSeconds}
                      />
                    </Box>
                  )}
                  {reactionPreview.messageType === "document" && (
                    <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <DocumentBox
                        documentUrl={reactionPreview.body}
                        documentFileName={reactionPreview.documentFileName}
                        messagePlatform={messagePlatform}
                      />
                    </Box>
                  )}
                  {reactionPreview.messageType === "location" && reactionPreview.body && (
                    <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Location size={20} color={theme.palette.primary.main} variant="Linear" />
                      <Typography variant="caption">Location</Typography>
                    </Box>
                  )}
                  {reactionPreview.messageType === "contacts" && reactionPreview.body && (
                    <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Personalcard size={20} color={theme.palette.primary.main} variant="Linear" />
                      <Typography variant="caption">Contact</Typography>
                    </Box>
                  )}
                  {(reactionPreview.messageType === "text" || !["image", "video", "audio", "document", "location", "contacts"].includes(reactionPreview.messageType)) && (
                    <Typography
                      sx={{
                        fontSize: "0.8125rem",
                        p: 1.25,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {reactionPreview.body || "Message"}
                    </Typography>
                  )}
                </Box>
              )}
              {reaction?.emoji && (
                <Typography component="span" sx={{ fontSize: "1.125rem", lineHeight: 1 }} aria-label="Reaction">
                  {reaction.emoji}
                </Typography>
              )}
            </Box>
          )}
          {replyBody && (
            <Box
              sx={{
                // WhatsApp/CRM-standard quote-preview treatment: a slim
                // accent rule + muted fill, not a boxed-in card — the
                // previous 6px border + heavy grey panel + (for text) a
                // solid neon-green inner fill with zero truncation let a
                // long quoted message balloon into a giant untruncated
                // block instead of reading as a compact preview strip
                // (Rahim, 2026-08-29: "see how bad it is").
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.045)"
                    : "rgba(0,0,0,0.035)",
                padding: { xs: "4px 6px", sm: "4px 7px", md: "5px 8px", lg: "5px 8px", xl: "5px 8px" },
                borderRadius: "4px",
                mb: { xs: 0.375, sm: 0.5, md: 0.75, lg: 0.75, xl: 0.75 },
                cursor: "pointer",
                borderLeft: (theme) => `3px solid ${theme.palette.primary.main}`,
                display: "flex",
                flexDirection: "column",
                gap: "1px",
                minWidth: 0,
                // Was width:"100%" (Rahim, 2026-09-09: "the grey box is not at
                // full width") — flush edge-to-edge with the bubble, no margin
                // at all. Corrected 2026-09-12 (Rahim: "the pill should show
                // right space showing the white space", confirmed for any
                // length of quoted text) — a fixed right margin so the white
                // bubble is always visible as a frame around the pill, like
                // WhatsApp's own quote-preview, instead of the pill and the
                // bubble being indistinguishable edges.
                width: "calc(100% - 16px)",
                // The bubble's own maxWidth falls back to "auto" (no cap at
                // all) for short-text messages — fine normally, but a reply
                // preview's quoted content isn't short, so it was stretching
                // the whole bubble near-full-width regardless of how short
                // the actual new reply text was (Rahim, 2026-08-29: "taking
                // literaly the full chat box"). Same width formula this file
                // already uses elsewhere, capped to the quote strip itself
                // so it can't do that no matter what the bubble around it does.
                maxWidth: "min(360px, calc(var(--chatbox-width, 100vw) - 96px))",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onReplyClick(replyId);
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: (theme) => theme.palette.primary.main,
                  fontSize: { xs: '0.6875rem', sm: '0.6875rem', md: '0.75rem', lg: '0.75rem', xl: '0.75rem' },
                }}
              >
                {replySenderName || "Unknown"}
              </Typography>
              {replyType === "image" && <ImageViewer imageUrl={replyBody} />}
              {replyType === "video" && <VideoBox videoUrl={replyBody} />}
              {replyType === "location" && replyBody && (() => {
                try {
                  const loc = typeof replyBody === "string" ? JSON.parse(replyBody) : replyBody;
                  const lat = Number(loc?.latitude);
                  const lng = Number(loc?.longitude);
                  if (Number.isNaN(lat) || Number.isNaN(lng)) {
                    return (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Location
                      </Typography>
                    );
                  }
                  return (
                    <Typography
                      variant="caption"
                      component="a"
                      href={`https://www.google.com/maps?q=${lat},${lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                    >
                      📍 {lat.toFixed(4)}, {lng.toFixed(4)}
                    </Typography>
                  );
                } catch {
                  return (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Location
                    </Typography>
                  );
                }
              })()}
              {replyType === "contacts" && replyBody && (() => {
                try {
                  const parsed = typeof replyBody === "string" ? JSON.parse(replyBody) : replyBody;
                  const list = parsed?.contacts;
                  if (!Array.isArray(list) || !list[0]) {
                    return (
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Contact
                      </Typography>
                    );
                  }
                  const nm = formatSharedContactDisplayName(list[0]);
                  return (
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                      Contact · {nm}
                    </Typography>
                  );
                } catch {
                  return (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Contact
                    </Typography>
                  );
                }
              })()}
              {replyType === "text" && (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    // No inner fill — the outer strip's own muted background
                    // is the only panel; a real quote preview reads as
                    // recessed/secondary text, not a second highlighted box
                    // stacked inside the first one.
                    color: (theme) => theme.palette.text.secondary,
                    fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.8125rem', lg: '0.8125rem', xl: '0.8125rem' },
                    fontWeight: 400,
                    // 2-line clamp — this was the actual bug: no truncation
                    // at all meant a long quoted message rendered in full,
                    // ballooning the whole card instead of previewing it.
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'normal',
                    '& span[data-emoji]': {
                      fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem', lg: '1rem', xl: '1rem' },
                      lineHeight: 1,
                      verticalAlign: 'middle',
                      display: 'inline-block',
                    }
                  }}
                >
                  {String(replyBody).split(/([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+)/u).map((part, idx) => {
                      const emojiRegex = /^[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+$/u;
                      if (part && emojiRegex.test(part)) {
                        return <span key={idx} data-emoji>{part}</span>;
                      }
                      return part;
                    })}
                </Typography>
              )}
              {replyType === "list" && replyBody && (() => {
                let listData;
                try {
                  listData = typeof replyBody === "string" ? JSON.parse(replyBody) : replyBody;
                } catch {
                  listData = { body: String(replyBody) };
                }
                return (
                  <Box sx={{
                    bgcolor: (theme) => theme.palette.mode === "dark" ? "#23272b" : "#e6f9ef",
                    borderRadius: 1,
                    p: 1,
                    mb: 0.5,
                    minWidth: 120,
                    maxWidth: 220,
                    textAlign: "left"
                  }}>
                    {listData.header && (
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#25D366" }}>
                        {listData.header}
                      </Typography>
                    )}
                    {/* Try to show selected row/option if available */}
                    {listData.selectedRowTitle && (
                      <Typography variant="body2" sx={{ color: "#13795B" }}>
                        {listData.selectedRowTitle}
                      </Typography>
                    )}
                    {/* Fallback: show body if no selected row */}
                    {!listData.selectedRowTitle && listData.body && (
                      <Typography 
                        variant="body2" 
                        component="div"
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {typeof listData.body === 'string' 
                          ? listData.body.split('\n').map((line, idx) => (
                              <React.Fragment key={idx}>
                                {line}
                                {idx < listData.body.split('\n').length - 1 && <br />}
                              </React.Fragment>
                            ))
                          : listData.body
                        }
                      </Typography>
                    )}
                  </Box>
                );
              })()}
              {replyType === "interactive" && replyBody && (() => {
  let buttonData;
  try {
    buttonData = typeof replyBody === "string" ? JSON.parse(replyBody) : replyBody;
    
  } catch {
    buttonData = { title: String(replyBody) };
  }

  return (
    <>
    {buttonData.buttons.map((btn, idx) => (
      <React.Fragment key={idx}>
    <Box sx={{
      bgcolor: (theme) => theme.palette.mode === "dark" ? "#23272b" : "#e6f9ef",
      borderRadius: 1,
      p: 1,
      mb: 0.5,
      minWidth: 120,
      maxWidth: 220,
      textAlign: "left"
    }}>
      {btn.title && (
        <Typography variant="body2" sx={{ color: "#00000", fontWeight: 500 }}>
          {btn.title}
        </Typography>
      )}
    </Box>
    </React.Fragment>
    ))}
    </>
  );
})()}

              {replyType === "template" && replyBody && (() => {
                let templateData;
                try {
                  templateData = typeof replyBody === "string" ? JSON.parse(replyBody) : replyBody;
                } catch {
                  templateData = {};
                }

                // Extract components
                const bodyComponent = (templateData.components || []).find(c => c.type === "BODY");
                const footerComponent = (templateData.components || []).find(c => c.type === "FOOTER");



                return (
                  <Box sx={{
                    bgcolor: (theme) => theme.palette.mode === "dark" ? "#23272b" : "#e6f9ef",
                    borderRadius: 1,
                    p: 1,
                    mb: 0.5,
                    minWidth: 120,
                    maxWidth: 220,
                    textAlign: "left"
                  }}>
                    {bodyComponent && bodyComponent.text && bodyComponent.text.split('\n').map((line, idx) => (
                      <Typography variant="body2" sx={{ color: "#13795B", fontWeight: 500 }} key={idx}>
                        {line}
                      </Typography>
                    ))}
                    {footerComponent && footerComponent.text && (
                      <Typography variant="caption" sx={{ color: "#888", display: "block", textAlign: "left" }}>
                        {footerComponent.text}
                      </Typography>
                    )}
                    
                  </Box>
                );
              })()}
              {replyType === "catalog" && replyBody && (() => {
                let catalogData;
                try {
                  catalogData = typeof replyBody === "string" ? JSON.parse(replyBody) : replyBody;
                } catch {
                  catalogData = {};
                }

                const { productData } = catalogData;
                const { products = [], bodyText, catalogName } = productData || {};

                return (
                  <Box sx={{
                    bgcolor: (theme) => theme.palette.mode === "dark" ? "#23272b" : "#e6f9ef",
                    borderRadius: 1,
                    p: 1,
                    mb: 0.5,
                    minWidth: 120,
                    maxWidth: 220,
                    textAlign: "left"
                  }}>
                    <Typography variant="body2" sx={{ color: "#13795B", fontWeight: 500, mb: 0.5 }}>
                      📦 {catalogName || 'Catalog'}
                    </Typography>
                    {bodyText && (
                      <Typography variant="body2" sx={{ color: "#13795B", fontSize: "0.8rem" }}>
                        {bodyText}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: "#888", fontSize: "0.7rem" }}>
                      {products.length} product{products.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                );
              })()}
            </Box>
          )}

          {/* CRITICAL: Render images FIRST before text, to prevent URLs from being rendered as text */}
          {messageType === "image" && (
            <>
              {(message && message.trim()) || metaMediaId ? (
                // CRITICAL: Check if message is JSON (template body) - don't render as image
                (() => {
                  // No URL yet but metaMediaId present — render ImageViewer for lazy download
                  const isPlaceholderBody = /^\[(image|video|audio|document|attachment)\]$/i.test(message?.trim() || '');
                  if ((!message || !message.trim() || isPlaceholderBody) && metaMediaId) {
                    return (
                      <ImageViewer
                        imageUrl={null}
                        mediaThumbnailUrl={mediaThumbnailUrl}
                        mediaFileSizeBytes={mediaFileSizeBytes}
                        contactId={contactId}
                        messageId={messageId}
                        metaMediaId={metaMediaId}
                      />
                    );
                  }
                  const trimmed = typeof message === 'string' ? message.trim() : '';
                  // Facebook/Instagram placeholder (e.g. "[image]") – not a URL; show placeholder
                  const isPlaceholder = /^\[(image|video|audio|document|attachment)\]$/i.test(trimmed);
                  const isJsonString = typeof message === 'string' &&
                    (trimmed.startsWith('{') || (trimmed.startsWith('[') && !isPlaceholder));

                  if (isPlaceholder) {
                    return (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        Image
                      </Typography>
                    );
                  }
                  if (isJsonString) {
                    // Check if it's a template JSON structure (WhatsApp components/name or Messenger template_type)
                    try {
                      const parsed = JSON.parse(message);
                      if (parsed && typeof parsed === "object") {
                        if (parsed.template_type && !parsed.components) {
                          return (
                            <TemplateMessageDisplay
                              templateBody={message}
                              fromMe={fromMe}
                              platform={messagePlatform}
                              inboxStructuredTemplateSizing
                            />
                          );
                        }
                        if (parsed.components || parsed.name) {
                          return (
                            <TemplateMessageDisplay
                              templateBody={message}
                              fromMe={fromMe}
                              platform={messagePlatform}
                              inboxStructuredTemplateSizing
                            />
                          );
                        }
                      }
                    } catch (e) {
                      // Not valid JSON, continue with image rendering
                    }

                    // If JSON but not template structure, show warning
                    console.warn("⚠️ JSON string detected in image renderer, but not a template structure");
                    return (
                      <Typography variant="body2" sx={{ color: 'warning.main', fontStyle: 'italic' }}>
                        Invalid image URL (JSON detected)
                      </Typography>
                    );
                  }

                  return (
                    <ImageViewer
                      imageUrl={message}
                      mediaThumbnailUrl={mediaThumbnailUrl}
                      mediaFileSizeBytes={mediaFileSizeBytes}
                      contactId={contactId}
                      messageId={messageId}
                      metaMediaId={metaMediaId}
                    />
                  );
                })()
              ) : (
                <Typography variant="body2" sx={{ color: 'error', fontStyle: 'italic' }}>
                  Image URL missing (message: {message ? typeof message : 'null/undefined'})
                </Typography>
              )}
            </>
          )}
          {messageType === "imageCollage" && message && (
            <MediaCollage media={message} fromMe={fromMe} mediaType="image" />
          )}
          {showWhatsAppLocation && whatsappLocation && (() => {
            const { latitude: lat, longitude: lng, name: placeName, address: placeAddress } = whatsappLocation;
            const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
            const staticMapUrl = `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=16&size=600x280&maptype=mapnik&markers=${lat},${lng},red-pushpin`;
            const coordLine = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
            // Redesigned (Rahim, 2026-08-27: "proper spacing and everything, use better icon
            // as well and font weight") to match the fixed-light "document" card convention
            // every other structured bubble (catalog/list/document) already uses this
            // session: same 380px width cap, #FFFFFF/#DADCDE/#050505/#65676B/#8A8D91 palette
            // instead of theme-following tokens, the same eyebrow-label-with-icon pattern,
            // and "Open in Maps" promoted from a plain text link into the same full-width
            // divided CTA row style as catalog's "Show More" button.
            return (
              <Box
                sx={{
                  // Smaller still — Rahim: "still a little too big make it more smaller"
                  // (2026-08-27) — below even Catalog/List's 380/340px now, at 300px.
                  maxWidth: {
                    xs: "min(calc((var(--chatbox-width, 100vw)) * 0.6), calc(100vw - 80px))",
                    sm: "min(300px, calc(var(--chatbox-width, 100vw) - 96px))",
                  },
                  // `width:"100%"` + `minWidth` — needed regardless of target size, see the
                  // shrink-wrap-ancestor root cause noted when the size bug was actually found.
                  width: "100%",
                  minWidth: { xs: 0, sm: 270 },
                  boxSizing: "border-box",
                  borderRadius: "14px",
                  overflow: "hidden",
                  border: "1px solid #DADCDE",
                  bgcolor: "#FFFFFF",
                  // Extra room below the card itself, on top of the shared pill-bubble bottom
                  // padding — the "Open in Maps" CTA sat right against the date row otherwise
                  // (Rahim, 2026-08-27: "the space above the date is very less").
                  mb: "4px",
                }}
              >
                {/* Media area now matches TemplateMessageDisplay's own header-image pattern
                    exactly: `aspectRatio: "4/3"` (scales with the card, not a hardcoded px
                    height) and — since the OpenStreetMap static tile can fail to load — the
                    SAME muted `#F0F2F5` + small `#65676B` icon fallback templates use for a
                    missing image, instead of a big colored pin illustration (Rahim, 2026-08-27:
                    "we dont use this sort of height... use better location icon i dont like
                    it use what we did for catelogue and templates"). */}
                {/* `position:relative` + `aspectRatio` live on this wrapper, not the <img>
                    itself, with the image/fallback both absolutely filling it — the "container
                    changes shape for a second" glitch (Rahim, 2026-08-27) was the <img>'s own
                    aspect-ratio box collapsing to the browser's small intrinsic broken-image
                    size for a moment while it was failing to load, before onError swapped in
                    the fallback; the size now comes from an element that's never broken. */}
                <Box
                  component="a"
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "block",
                    position: "relative",
                    // Inset + its own corner radius instead of flush edge-to-edge (Rahim,
                    // 2026-08-27: "add corner radius to the background grey one like we did
                    // here" — the reference shows the placeholder framed inside the white
                    // card with margin all around, matching TemplateMessageDisplay's own
                    // header-image treatment, not relying on the outer card's overflow:hidden
                    // clip for rounding).
                    margin: "12px 12px 0 12px",
                    width: "auto",
                    // Scaled back down with the card itself — same 4:3 TemplateMessageDisplay
                    // convention (Rahim: "make it our basic one").
                    aspectRatio: "4 / 3",
                    borderRadius: "10px",
                    textDecoration: "none",
                    bgcolor: "#F0F2F5",
                    overflow: "hidden",
                  }}
                >
                  {!locationMapError ? (
                    <Box
                      component="img"
                      src={staticMapUrl}
                      alt={`Map preview for ${coordLine}`}
                      onError={() => setLocationMapError(true)}
                      sx={{
                        display: "block",
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        // The actual glitch (Rahim, 2026-08-27: "fix this weird error... for
                        // every type not just location"): a slow/failing <img> renders its alt
                        // text as a blue underlined link (inherited from the wrapping <a>)
                        // while it's stuck pending, before `onError` ever fires. `color`
                        // governs that fallback text's paint color (not the image itself, once
                        // it loads) — making it transparent hides the glitch text completely
                        // without touching the `alt` attribute screen readers still get.
                        color: "transparent",
                      }}
                    />
                  ) : (
                    // Rahim, 2026-08-27: "remove the icon border the black one" — dropped the
                    // outlined badge box, plain centered icon again.
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Location size={30} color="#65676B" variant="Linear" />
                    </Box>
                  )}
                </Box>

                {/* Eyebrow label — same icon+uppercase-muted pattern as Catalog's "Products
                    (n)" section label, not a second heading. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", padding: "10px 14px 4px 14px" }}>
                  <Location size="14" color="#8A8D91" variant="Linear" />
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.6875rem",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      color: "#8A8D91",
                    }}
                  >
                    Location
                  </Typography>
                </Box>

                <Box sx={{ px: "14px", pb: "12px" }}>
                  {/* Place name is the real heading when present (same weight/size as every
                      other card's title — see TemplateMessageDisplay's HEADER TEXT); the raw
                      coordinates drop to a small monospace caption underneath instead of being
                      the primary line, unless there's no place name to lead with at all. */}
                  {placeName ? (
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9375rem",
                        color: "#050505",
                        lineHeight: 1.35,
                        wordBreak: "break-word",
                      }}
                    >
                      {placeName}
                    </Typography>
                  ) : (
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9375rem",
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                        color: "#050505",
                        lineHeight: 1.35,
                        wordBreak: "break-word",
                      }}
                    >
                      {coordLine}
                    </Typography>
                  )}
                  {placeAddress && (
                    <Typography sx={{ mt: 0.375, fontSize: "0.8125rem", fontWeight: 400, color: "#65676B", lineHeight: 1.35 }}>
                      {placeAddress}
                    </Typography>
                  )}
                  {placeName && (
                    <Typography
                      sx={{
                        mt: 0.375,
                        fontSize: "0.75rem",
                        fontWeight: 400,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                        color: "#8A8D91",
                      }}
                    >
                      {coordLine}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ borderColor: "#E6E7EB" }} />
                <Typography
                  component="a"
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    boxSizing: "border-box",
                    padding: "10px 14px",
                    color: "#1F7A5C",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    textDecoration: "none",
                    "&:hover": { backgroundColor: "#1F7A5C14" },
                  }}
                >
                  Open in Maps
                  <Box sx={{ display: "flex", ml: 0.5 }}>
                    <Export size="16" color="currentColor" variant="Linear" />
                  </Box>
                </Typography>
              </Box>
            );
          })()}
          {/* Redesigned (Rahim, 2026-08-27: "so bad make it better increase size") — same
              fixed-light "document" card + real committed width as everything else this
              session, instead of theme-following tokens and a flat non-responsive
              `maxWidth:300` that was subject to the same shrink-wrap-ancestor collapse the
              location card had (no dedicated maxWidth case for messageType "contacts" in
              ChatCard's outer pill either). */}
          {showSharedContacts && whatsappSharedContacts && (
            <Box
              sx={{
                maxWidth: {
                  xs: "min(calc((var(--chatbox-width, 100vw)) * 0.7), calc(100vw - 72px))",
                  sm: "min(380px, calc(var(--chatbox-width, 100vw) - 96px))",
                },
                width: "100%",
                minWidth: { xs: 0, sm: 300 },
                boxSizing: "border-box",
                borderRadius: "14px",
                overflow: "hidden",
                border: "1px solid #DADCDE",
                bgcolor: "#FFFFFF",
              }}
            >
              {/* Eyebrow label — same icon+uppercase-muted pattern as every other card's
                  section label. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", padding: "10px 14px" }}>
                <Personalcard size="14" color="#8A8D91" variant="Linear" />
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.6875rem",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: "#8A8D91",
                  }}
                >
                  Contact
                </Typography>
              </Box>
              <Divider sx={{ borderColor: "#E6E7EB" }} />
              {whatsappSharedContacts.contacts.map((c, idx) => {
                const displayName = formatSharedContactDisplayName(c);
                const avatarProps = stringAvatar(displayName, 48);
                const phones = Array.isArray(c.phones) ? c.phones : [];
                const emails = Array.isArray(c.emails) ? c.emails : [];
                const primary = primaryPhonePayloadForInbox(c);
                const rowKey = `vcard-${idx}-${primary?.phoneNumberID || displayName}`;
                const isOpening = openingSharedContactKey === rowKey;
                return (
                  <React.Fragment key={rowKey}>
                    {idx > 0 && <Divider sx={{ borderColor: "#E6E7EB" }} />}
                    <Box
                      role={primary ? "button" : undefined}
                      tabIndex={primary ? 0 : undefined}
                      onClick={() => primary && openInboxChatForSharedContact(c, rowKey)}
                      onKeyDown={(e) => {
                        if (!primary) return;
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openInboxChatForSharedContact(c, rowKey);
                        }
                      }}
                      sx={{
                        padding: "12px 14px",
                        cursor: primary ? "pointer" : "default",
                        transition: "background-color 0.15s ease",
                        ...(primary
                          ? {
                              "&:hover": { backgroundColor: "#1F7A5C0A" },
                              "&:focus-visible": { outline: "2px solid #1F7A5C", outlineOffset: -2 },
                            }
                          : {}),
                        opacity: isOpening ? 0.8 : 1,
                      }}
                    >
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                        <Avatar {...avatarProps} sx={{ ...avatarProps.sx, width: 48, height: 48 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.9375rem",
                                color: "#050505",
                                lineHeight: 1.35,
                                wordBreak: "break-word",
                              }}
                            >
                              {displayName}
                            </Typography>
                            {isOpening && <CircularProgress size={14} thickness={5} />}
                          </Box>
                          {phones.map((ph, pi) => {
                            const label = ph.phone || ph.wa_id || "";
                            return (
                              <Box
                                key={`ph-${pi}`}
                                sx={{ mt: pi === 0 ? 0.5 : 0.375, display: "flex", alignItems: "flex-start", gap: 0.5 }}
                              >
                                <Box sx={{ display: "flex", mt: 0.15, flexShrink: 0 }}>
                                  <Call size={16} color="#1F7A5C" variant="Linear" />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "#050505", wordBreak: "break-all" }}>
                                    {label}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                          {primary && (
                            <Typography sx={{ display: "block", mt: 0.5, fontSize: "0.75rem", color: "#8A8D91" }}>
                              {isOpening ? "Opening…" : "Tap to open chat in inbox"}
                            </Typography>
                          )}
                          {emails.map((em, ei) =>
                            em.email ? (
                              <Typography
                                key={`em-${ei}`}
                                component="a"
                                href={`mailto:${em.email}`}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                sx={{
                                  display: "block",
                                  mt: 0.375,
                                  fontSize: "0.75rem",
                                  color: "#1F7A5C",
                                  textDecoration: "none",
                                  wordBreak: "break-all",
                                  "&:hover": { textDecoration: "underline" },
                                }}
                              >
                                {em.email}
                              </Typography>
                            ) : null
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          )}
          {/* CRITICAL: Handle text messages - render if messageType is "text" OR "chat" (when we have a string message) */}
          {/* "chat" is used as the type for ChatBody, but messageType should be "text" for text messages */}
          {/* However, sometimes messageType gets set to "chat" instead of "text", so we handle both */}
          {/* IMPORTANT: Only render as text if messageType is NOT "image", "video", "audio", "document" */}
          {(() => {
            // Don't render as text if it's explicitly a media type
            if (
              showWhatsAppLocation ||
              showSharedContacts ||
              (messageType &&
                ["image", "video", "audio", "document", "location", "contacts", "template", "catalog", "list", "post_comment", "live_comment"].includes(
                  messageType
                )) ||
              interactiveButtonsPayload
            ) {
              return null;
            }
            
            // Consider it a text message if:
            // 1. messageType is "text" (correct)
            // 2. messageType is "chat" AND we have a string message AND it's NOT a URL (fallback)
            // 3. messageType is missing/undefined/null/empty AND we have a string message AND it's NOT a URL
            const isTextMessage = messageType === "text" || 
                                 (messageType === "chat" &&  message && !interactiveButtonsPayload && typeof message === 'string' && !message.match(/^https?:\/\//)) ||
                                 ((!messageType || messageType === null || messageType === undefined || messageType === "") && 
                                  message && typeof message === 'string' && !message.match(/^https?:\/\//));
            const hasValidMessage = message && typeof message === 'string' && message.trim();
            
            if (isTextMessage && hasValidMessage) {
              // Use black text for automated messages (same as interactive buttons), otherwise use theme color
              const textColor = (fromMe || isAutomatedMessage) ? "#000000" : (theme) => theme.palette.text.primary;
              
              return (
                <Box>
                  <Typography
                    ref={textMessageRef}
                    variant="body2"
                    component="div"
                    sx={{
                      color: textColor,
                      // Inbox/Dashboard row-title token (same as the conversation list contact
                      // name, MessageCard.jsx), flat at all breakpoints — not the generic app body size.
                      fontSize: "0.9375rem",
                      whiteSpace: 'pre-wrap', // Preserve line breaks and whitespace
                      wordBreak: 'break-word',
                      // Clamped by default — expanding removes all four clamp
                      // properties rather than just bumping the line count, so a
                      // fully-expanded message isn't still constrained to a box model.
                      ...(isTextExpanded
                        ? {}
                        : {
                            display: '-webkit-box',
                            WebkitLineClamp: TEXT_MESSAGE_CLAMP_LINES,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }),
                      '& span[data-emoji]': {
                        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem', lg: '1.25rem', xl: '1.25rem' },
                        lineHeight: 1,
                        verticalAlign: 'middle',
                        display: 'inline-block',
                      }
                    }}
                  >
                    {String(message).split(/([\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+)/u).map((part, idx) => {
                      const emojiRegex = /^[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]+$/u;
                      if (part && emojiRegex.test(part)) {
                        return <span key={idx} data-emoji>{part}</span>;
                      }
                      // Apply mention highlighting for ALL messages (both internal mentions and user mentions)
                      return <span key={idx}>{renderMessageWithMentions(part)}</span>;
                    })}
                  </Typography>
                  {isTextClamped && (
                    <Typography
                      component="button"
                      type="button"
                      onClick={() => setIsTextExpanded((v) => !v)}
                      sx={{
                        display: 'block',
                        mt: 0.25,
                        border: 'none',
                        background: 'none',
                        p: 0,
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        // `all:"unset"` isn't used here — it silently drops `color`
                        // on a Typography component="button" (see ui-gotchas memory,
                        // CatalogMessageCard/ListMessageCard incident) — only reset
                        // the specific native-button defaults actually needed.
                        // Brand green in both modes (Rahim, 2026-08-27: "for readmore
                        // use green colour for dark mood and light mood") — theme.palette
                        // primary.main already resolves correctly per mode (promoted to
                        // the lighter #4FC389 shade for dark mode specifically, see
                        // inbox-conversation-card-restructure), same token used everywhere
                        // else in the app for this accent, not a new hardcoded hex.
                        color: (theme) => theme.palette.primary.main,
                        '&:hover': {
                          color: (theme) => theme.palette.primary.dark,
                        },
                      }}
                    >
                      {isTextExpanded ? "Show less" : "Read more"}
                    </Typography>
                  )}
                </Box>
              );
            }
            
            return null;
          })() }
          {messageType === "video" && (() => {
            const isPlaceholderBody = /^\[(image|video|audio|document|attachment)\]$/i.test(message?.trim() || '');
            const hasUrl = message && message.trim() && !isPlaceholderBody;
            if (hasUrl) {
              return <VideoBox videoUrl={message} mediaThumbnailUrl={mediaThumbnailUrl} mediaFileSizeBytes={mediaFileSizeBytes} contactId={contactId} messageId={messageId} metaMediaId={metaMediaId} />;
            }
            if (metaMediaId) {
              return <VideoBox videoUrl={null} mediaThumbnailUrl={mediaThumbnailUrl} mediaFileSizeBytes={mediaFileSizeBytes} contactId={contactId} messageId={messageId} metaMediaId={metaMediaId} />;
            }
            return <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Video</Typography>;
          })()}
          {messageType === "videoCollage" && message && (
            <MediaCollage media={message} fromMe={fromMe} mediaType="video" />
          )}
          {messageType === "audio" && (() => {
            const isPlaceholderBody = /^\[(image|video|audio|document|attachment)\]$/i.test(message?.trim() || '');
            const hasUrl = message && message.trim() && !isPlaceholderBody;
            if (hasUrl) {
              return <AudioBox audioUrl={message} initialDurationSeconds={audioDurationSeconds} mediaFileSizeBytes={mediaFileSizeBytes} fromMe={fromMe} contactId={contactId} messageId={messageId} metaMediaId={metaMediaId} />;
            }
            if (metaMediaId) {
              return <AudioBox audioUrl={null} initialDurationSeconds={audioDurationSeconds} mediaFileSizeBytes={mediaFileSizeBytes} fromMe={fromMe} contactId={contactId} messageId={messageId} metaMediaId={metaMediaId} />;
            }
            return <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Audio</Typography>;
          })()}
          {messageType === "document" && (() => {
            const isPlaceholderBody = /^\[(image|video|audio|document|attachment)\]$/i.test(message?.trim() || '');
            const hasUrl = message && message.trim() && !isPlaceholderBody;
            if (hasUrl) {
              return <DocumentBox documentUrl={message} documentFileName={documentFileName} mediaThumbnailUrl={mediaThumbnailUrl} mediaFileSizeBytes={mediaFileSizeBytes} messagePlatform={messagePlatform} contactId={contactId} messageId={messageId} metaMediaId={metaMediaId} />;
            }
            if (metaMediaId) {
              return <DocumentBox documentUrl={null} documentFileName={documentFileName} mediaThumbnailUrl={mediaThumbnailUrl} mediaFileSizeBytes={mediaFileSizeBytes} messagePlatform={messagePlatform} contactId={contactId} messageId={messageId} metaMediaId={metaMediaId} />;
            }
            return <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>Document</Typography>;
          })()}
          {["image", "video", "document"].includes(messageType) && caption && typeof caption === "string" && caption.trim() && (
            <Typography
              variant="body2"
              component="div"
              sx={{
                // Rahim, 2026-08-27: "the gap between the image and the text is not good add
                // more gap" — was 0.5 (4px), read as glued to the image edge.
                mt: 1.25,
                color: (fromMe || isAutomatedMessage) ? "#000000" : (theme) => theme.palette.text.primary,
                fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem', lg: '0.875rem', xl: '0.875rem' },
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {caption}
            </Typography>
          )}
          {messageType === "template" && (
            message ? (
              <TemplateMessageDisplay
                templateBody={message}
                fromMe={fromMe}
                platform={messagePlatform}
                inboxStructuredTemplateSizing
              />
            ) : (
              <Typography variant="body2" sx={{ color: 'error', fontStyle: 'italic' }}>
                Template data missing
              </Typography>
            )
          )}

          {messageType === "catalog" && (() => {
            // Same shared-scaffold move as "template" — was a fully custom row built
            // directly in ChatBody.jsx before (Rahim, 2026-08-25: "render it under the
            // template chat... each and every case").
            let catalogData;
            try {
              catalogData = typeof message === "string" ? JSON.parse(message) : message;
            } catch (e) {
              catalogData = {
                catalogType: "single",
                productData: { bodyText: "Catalog message", products: [], catalogName: "Catalog" },
              };
            }
            return <CatalogMessageCard data={catalogData} />;
          })()}

          {messageType === "list" && (() => {
            let listData;
            try {
              listData = typeof message === "string" ? JSON.parse(message) : message;
            } catch (e) {
              listData = { body: String(message || "List message") };
            }
            return <ListMessageCard data={listData} />;
          })()}

          {messageType === "catalog_purchase" && (() => {
            // Parse the complete product data from the message body (JSON string)
            let productDataToUse;
            
            try {
              // Parse the message as JSON (it contains the complete product data)
              productDataToUse = typeof message === "string" ? JSON.parse(message) : message;
            } catch (parseError) {
              // If parsing fails, create a fallback
              productDataToUse = {
                title: "Unknown Product",
                description: "",
                price: 0,
                currency: "USD",
                quantity: 1,
                imageUrl: "",
                productName: "Unknown Product",
                availability: "in_stock",
                isMultipleItems: false,
                items: []
              };
            }


            // Redesigned (Rahim, 2026-08-27: "for the product order use better icons as well
            // spacing and everything like we did for the catelogue templates etc") — same
            // fixed-light "document" palette/width/typography every other structured card
            // this session uses instead of the old theme-conditional isDark hex pairs and
            // literal WhatsApp green (#25D366).
            const hasImage = productDataToUse.imageUrl && productDataToUse.imageUrl.trim() !== '' && productDataToUse.imageUrl !== 'null' && productDataToUse.imageUrl !== 'undefined';
            // Meta returns this as "in stock"/"out of stock" (space-separated), but some
            // payloads use "in_stock" — normalize both so the chip doesn't invert.
            const inStock = String(productDataToUse.availability || 'in_stock')
              .toLowerCase()
              .replace(/_/g, ' ')
              .trim() === 'in stock';
            const items = Array.isArray(productDataToUse.items) ? productDataToUse.items : [];
            const hasMultipleItems = productDataToUse.isMultipleItems && items.length > 1;

            return (
              <Box
                sx={{
                  maxWidth: {
                    xs: "min(calc((var(--chatbox-width, 100vw)) * 0.7), calc(100vw - 72px))",
                    sm: "min(380px, calc(var(--chatbox-width, 100vw) - 96px))",
                  },
                  width: "100%",
                  minWidth: { xs: 0, sm: 260 },
                  boxSizing: "border-box",
                  borderRadius: "14px",
                  overflow: "hidden",
                  border: "1px solid #DADCDE",
                  bgcolor: "#FFFFFF",
                }}
              >
                {/* Header — same icon+heading treatment as Catalog's own header row. */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, padding: "14px 14px 10px 14px" }}>
                  <ShoppingCart size={20} color="#1F7A5C" variant="Bold" />
                  <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem", color: "#050505" }}>
                    Product Order
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: "#E6E7EB" }} />

                {/* Product row */}
                <Box sx={{ display: "flex", gap: 1.5, padding: "12px 14px" }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "10px",
                      bgcolor: "#F0F2F5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {hasImage ? (
                      <Box
                        component="img"
                        src={productDataToUse.imageUrl}
                        alt={productDataToUse.title || "Product"}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", color: "transparent" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <Gallery size={24} color="#8A8D91" variant="Linear" />
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#050505",
                        lineHeight: 1.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {productDataToUse.productName || productDataToUse.title || "Unknown Product"}
                    </Typography>

                    {productDataToUse.description && (
                      <Typography
                        sx={{
                          mt: 0.25,
                          color: "#65676B",
                          fontSize: "0.75rem",
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {productDataToUse.description}
                      </Typography>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75 }}>
                      {(productDataToUse.price !== undefined && productDataToUse.price !== null) && (
                        <Typography sx={{ color: "#1F7A5C", fontWeight: 600, fontSize: "0.9375rem" }}>
                          {productDataToUse.currency && productDataToUse.currency !== "USD" ? productDataToUse.currency : "$"}
                          {productDataToUse.price}
                        </Typography>
                      )}
                      {productDataToUse.quantity && productDataToUse.quantity > 1 && (
                        <Typography sx={{ color: "#65676B", fontSize: "0.75rem", fontWeight: 500 }}>
                          Qty: {productDataToUse.quantity}
                        </Typography>
                      )}
                    </Box>

                    {/* Availability chip — same soft-tint pattern as Catalog's stock chip. */}
                    {productDataToUse.availability && (
                      <Chip
                        label={inStock ? "In Stock" : "Out of Stock"}
                        size="small"
                        sx={{
                          mt: 0.75,
                          height: 20,
                          fontSize: "0.625rem",
                          fontWeight: 500,
                          backgroundColor: inStock ? "#EAF3EF" : "#FDF3E7",
                          color: inStock ? "#1F7A5C" : "#9A6700",
                          "& .MuiChip-label": { px: "6px" },
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Multi-item expand — same full-width divided CTA-row button as Catalog's
                    "Show More". */}
                {hasMultipleItems && (
                  <>
                    <Divider sx={{ borderColor: "#E6E7EB" }} />
                    <Typography
                      component="button"
                      onClick={() => setShowItems(!showItems)}
                      sx={{
                        border: "none",
                        outline: "none",
                        background: "none",
                        margin: 0,
                        font: "inherit",
                        textAlign: "inherit",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "10px 14px",
                        color: "#1F7A5C",
                        fontSize: "0.8125rem",
                        fontWeight: 500,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#1F7A5C14" },
                      }}
                    >
                      {showItems ? "Show Less" : `View all ${productDataToUse.totalItems || items.length} items`}
                      <Box sx={{ display: "flex", ml: 0.5 }}>
                        {showItems ? (
                          <ArrowUp2 size="16" color="currentColor" variant="Linear" />
                        ) : (
                          <ArrowDown2 size="16" color="currentColor" variant="Linear" />
                        )}
                      </Box>
                    </Typography>

                    <Collapse in={showItems}>
                      {items.map((item, index) => {
                        const itemHasImage =
                          item.image_url && item.image_url.trim() !== '' && item.image_url !== 'null' && item.image_url !== 'undefined';
                        return (
                          <React.Fragment key={index}>
                            <Divider sx={{ borderColor: "#E6E7EB" }} />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, padding: "10px 14px" }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "8px",
                                  bgcolor: "#F0F2F5",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                }}
                              >
                                {itemHasImage ? (
                                  <Box
                                    component="img"
                                    src={item.image_url}
                                    alt={item.name || "Product"}
                                    sx={{ width: "100%", height: "100%", objectFit: "cover", color: "transparent" }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <Gallery size={16} color="#8A8D91" variant="Linear" />
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 500, fontSize: "0.8125rem", color: "#050505" }}>
                                  {item.name || item.product_retailer_id || "Unknown Product"}
                                </Typography>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.25 }}>
                                  <Typography sx={{ fontSize: "0.6875rem", color: "#8A8D91" }}>
                                    Qty: {item.quantity || 1}
                                  </Typography>
                                  <Typography sx={{ color: "#1F7A5C", fontWeight: 600, fontSize: "0.75rem" }}>
                                    {item.currency || "PKR"}{item.item_price || 0}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </React.Fragment>
                        );
                      })}
                    </Collapse>
                  </>
                )}
              </Box>
            );
          })()}
          {/* Interactive Buttons */}
          {/* Redesigned (Rahim, 2026-08-27: "go fix it use our new ui") — same fixed-light
              "document" card every other structured bubble uses this session instead of the
              old theme-conditional isDark hex pairs and WhatsApp-shaped corners; buttons now
              match Catalog/Template's exact full-width divided CTA-row style. Dropped the
              dead `onClick={() => console.log(...)}` per button — it never did anything real;
              say the word if you want them actually wired to something. */}
          {interactiveButtonsPayload && (
            <Box
              sx={{
                maxWidth: {
                  xs: "min(calc((var(--chatbox-width, 100vw)) * 0.7), calc(100vw - 72px))",
                  sm: "min(380px, calc(var(--chatbox-width, 100vw) - 96px))",
                },
                width: "100%",
                minWidth: { xs: 0, sm: 260 },
                boxSizing: "border-box",
                borderRadius: "14px",
                overflow: "hidden",
                border: "1px solid #DADCDE",
                bgcolor: "#FFFFFF",
                alignSelf: fromMe ? "flex-end" : "flex-start",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              }}
            >
              {(interactiveButtonsPayload.header || interactiveButtonsPayload.body || interactiveButtonsPayload.footer) && (
                <Box sx={{ padding: "14px 14px 12px 14px" }}>
                  {/* Image header (WhatsApp Web workflows): only http(s) urls are drawn. */}
                  {interactiveButtonsPayload.header?.type === "image" && /^https?:\/\//i.test(String(interactiveButtonsPayload.header.imageUrl || "")) && (
                    <Box
                      component="img"
                      src={interactiveButtonsPayload.header.imageUrl}
                      alt=""
                      loading="lazy"
                      sx={{ display: "block", width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: "8px", mb: 1 }}
                    />
                  )}
                  {interactiveButtonsPayload.header?.text && (
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9375rem",
                        color: "#050505",
                        mb: interactiveButtonsPayload.body ? 0.5 : 0,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {interactiveButtonsPayload.header.text}
                    </Typography>
                  )}
                  {interactiveButtonsPayload.body && (
                    <Typography
                      sx={{
                        fontSize: "0.875rem",
                        color: "#050505",
                        lineHeight: 1.4,
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {interactiveButtonsPayload.body}
                    </Typography>
                  )}
                  {interactiveButtonsPayload.footer && (
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontSize: "0.75rem",
                        color: "#8A8D91",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {interactiveButtonsPayload.footer}
                    </Typography>
                  )}
                </Box>
              )}

              {/* More than 4 buttons collapse into one "Select an option" dropdown row, same as
                  the list message and the web-chat widget's buttons card. */}
              <Box>
                {interactiveButtonsPayload.buttons.length > 4 && (
                  <>
                    <Divider sx={{ borderColor: "#E6E7EB" }} />
                    <Typography
                      component="button"
                      onClick={() => setButtonsDropdownOpen((prev) => !prev)}
                      sx={{ ...interactiveButtonRowSx, "&:hover": { backgroundColor: "#1F7A5C14" } }}
                    >
                      <Box sx={{ display: "flex", mr: 1 }}>
                        <MenuIcon size="18" color="currentColor" variant="Linear" />
                      </Box>
                      Select an option
                      <Box sx={{ display: "flex", ml: 0.5 }}>
                        {buttonsDropdownOpen ? (
                          <ArrowUp2 size="16" color="currentColor" variant="Linear" />
                        ) : (
                          <ArrowDown2 size="16" color="currentColor" variant="Linear" />
                        )}
                      </Box>
                    </Typography>
                  </>
                )}
                {(interactiveButtonsPayload.buttons.length <= 4 || buttonsDropdownOpen) &&
                  interactiveButtonsPayload.buttons.map((btn, idx) => {
                    const isDropdownOption = interactiveButtonsPayload.buttons.length > 4;
                    return (
                      <React.Fragment key={btn.id || idx}>
                        <Divider sx={{ borderColor: "#E6E7EB" }} />
                        <Typography
                          component="button"
                          // CTA URL messages store the link on the button
                          onClick={btn.url ? () => window.open(btn.url, "_blank", "noopener,noreferrer") : undefined}
                          sx={{
                            ...interactiveButtonRowSx,
                            ...(isDropdownOption && { color: "#050505", fontWeight: 400, fontSize: "0.8125rem" }),
                            "&:hover": { backgroundColor: "#1F7A5C14" },
                          }}
                        >
                          {btn.description ? (
                            <Box sx={{ textAlign: "center" }}>
                              <Box component="span" sx={{ display: "block" }}>{btn.title}</Box>
                              <Box component="span" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 400, color: "text.secondary", mt: 0.25 }}>
                                {btn.description}
                              </Box>
                            </Box>
                          ) : (
                            btn.title
                          )}
                        </Typography>
                      </React.Fragment>
                    );
                  })}
              </Box>
            </Box>
          )}

            
          


          {(
            <Box
              display={"flex"}
              width={"100%"}
              justifyContent={"flex-end"}
              alignItems={pillGrouping ? "center" : undefined}
              gap={pillGrouping ? "6px" : { xs: "5px", sm: "5px", md: "5px", lg: "5px", xl: "4px" }}
              sx={
                // pillGrouping now covers templates too, including Meta-attachment ones — give
                // it priority so those get the same spacing as every other pill bubble instead
                // of the older, tighter meta-specific padding (Rahim, 2026-08-25: "the date and
                // tick icon... too close to the border... should be same as the whatsapp bubbles").
                pillGrouping
                  ? {
                      pr: 0.25,
                      pt: 1,
                      pb: 0.375,
                      // Never wrap onto a second line, no matter how narrow the bubble gets —
                      // date+tick either fit on one row or the bubble grows to fit them.
                      flexWrap: "nowrap",
                      flexShrink: 0,
                    }
                  : useMetaAttachmentBubble &&
                      (messagePlatform === "instagram" || messagePlatform === "facebook")
                    ? {
                        pr: { xs: 0.75, sm: 1, md: 1, lg: 1, xl: 1 },
                        boxSizing: "border-box",
                      }
                    : undefined
              }
            >
              <Typography
                variant="caption"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  // Use darker color for timestamps on light green background (automated messages)
                  color: (fromMe || isAutomatedMessage) ? "#555555" : (theme) => theme.palette.text.secondary,
                  fontSize: pillGrouping
                    ? "0.8125rem"
                    : { xs: "0.4375rem", sm: "0.5rem", md: "0.625rem", lg: "0.6875rem", xl: "0.625rem" },
                  // MUI's caption variant bakes in line-height:1.66 — against a 13px font that's
                  // ~22px of vertical box, versus the tick icon's plain 12px box. Both looked
                  // "centered" in the row but the text's own inflated line-height read as extra
                  // space under the date specifically. Tightening this is the actual fix.
                  // Remaining gap after that is normal font descender reserve (fonts keep empty
                  // space below the baseline even when the glyphs themselves have no descenders,
                  // like "PM") — line-height can't remove that, only a small position nudge can.
                  ...(pillGrouping
                    ? { lineHeight: 1.1, position: "relative", top: "1.5px", whiteSpace: "nowrap", flexShrink: 0 }
                    : {}),
                }}
              >
                {formatMessageTime(time)}
              </Typography>
              {status && !isInternal && fromMe && (
                <Box
                  sx={{
                    // These ticks are raw SVGs with hardcoded native px dimensions, and each
                    // status icon has a DIFFERENT native aspect ratio (Read 20:13, Delivered
                    // 21:16, Sent 15:13) — stretching all of them to one fixed box distorted
                    // them inconsistently relative to each other. Anchoring only the height and
                    // leaving width auto lets each icon scale by its own real aspect ratio.
                    // pending/error use square Iconsax icons instead (16px, a size bump from
                    // before), so they get their own slightly taller box.
                    width: pillGrouping ? "auto" : { xs: 8, sm: 8, md: 9.5, lg: 8, xl: 8 },
                    height: pillGrouping
                      ? (["pending", "error", "failed"].includes(String(status).toLowerCase().trim()) ? 16 : 12)
                      : { xs: 9, sm: 9, md: 10.5, lg: 9, xl: 9 },
                    display: "flex",
                    flexDirection: "column",
                    mt: pillGrouping ? 0 : { xs: "1px", sm: "1px", md: "1.75px", lg: "1px", xl: "1px" },
                    alignItems: "center",
                    justifyContent: "center",
                    ...(pillGrouping
                      ? {
                          flexShrink: 0,
                          "& svg": { width: "auto", height: "100%", display: "block", flexShrink: 0 },
                        }
                      : {}),
                  }}
                >
                  {renderStatusIcon(status, pillGrouping)}
                </Box>
              )}
            </Box>
          )}
          {showBubbleReaction && (
            <Box
              component="span"
              aria-label={`Reaction ${reaction.emoji}`}
              sx={{
                position: "absolute",
                bottom: -10,
                zIndex: 2,
                px: 0.25,
                py: 0,
                borderRadius: 0,
                bgcolor: "transparent",
                boxShadow: "none",
                border: "none",
                fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
                lineHeight: 1.25,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 22,
                minWidth: 22,
                textShadow: (t) =>
                  t.palette.mode === "dark"
                    ? "0 0 2px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.8)"
                    : "0 0 1px rgba(255,255,255,0.9), 0 1px 2px rgba(11,20,26,0.18)",
                filter: (t) => (t.palette.mode === "dark" ? "none" : "drop-shadow(0 0.5px rgba(11,20,26,0.12))"),
                ...(fromMe ? { left: 8 } : { right: 8 }),
              }}
            >
              {reaction.emoji}
            </Box>
          )}
        </Box>

      </Box>
    </Box>
  );
};

export default ChatCard;
